import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    DialogActions,
    Grid,
    Skeleton,
    TextField,
} from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
    PARCELLAIRE_TAB_BOX_STYLE,
    RADIOMETRIC_TARGET_NAMES,
} from "./constants";
import { formatDateTime, offset } from "../../utils/formatTime";
import { useMemo, useState } from "react";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import { BACKEND_ROUTES } from "../../backendRoutes";
import { FetchErrorAlert } from "../../components/FetchErrorAlert";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { MODEL_PROPTYPES } from "../../models";
import { PAGINATION } from "../../constants";
import PropTypes from "prop-types";
import download from "downloadjs";
import { saveMissionGeometry } from "./utils";
import { useFetch } from "../../hooks";
import useSWR from "swr";

RadiometricTargetSaveForm.propTypes = {
    setIsOpen: PropTypes.func.isRequired,
    radioMetricTargetPolygonRef: PropTypes.object.isRequired,
    openSnackbar: PropTypes.func.isRequired,
    orthoimageCompany: PropTypes.shape(MODEL_PROPTYPES.Company),
    orthoimageContract: PropTypes.shape(MODEL_PROPTYPES.Contract),
    orthoimageSite: PropTypes.shape(MODEL_PROPTYPES.Site),
    orthoimageMission: PropTypes.shape(MODEL_PROPTYPES.Mission),
    existingGeometry: PropTypes.object,
    refetchMissionGeometries: PropTypes.func,
};

export function RadiometricTargetSaveForm({
    setIsOpen,
    radioMetricTargetPolygonRef,
    openSnackbar,
    orthoimageCompany,
    orthoimageContract,
    orthoimageSite,
    orthoimageMission,
    existingGeometry,
    refetchMissionGeometries,
}) {
    const [loading, setLoading] = useState(false);
    const { patch, post } = useFetch();

    const { control, setValue, handleSubmit } = useForm({
        defaultValues: {
            company: orthoimageCompany || null,
            contract: orthoimageContract || null,
            site: orthoimageSite || null,
            mission: orthoimageMission || null,
            radioMetricTargetName:
                existingGeometry?.name ||
                RADIOMETRIC_TARGET_NAMES.RADIOMETRIC_TARGET_2024,
        },
    });

    const [
        companyWatch,
        contractWatch,
        siteWatch,
        missionWatch,
        radioMetricTargetNameWatch,
    ] = useWatch({
        name: [
            "company",
            "contract",
            "site",
            "mission",
            "radioMetricTargetName",
        ],
        control,
    });

    const { data: companies, error: companiesFetchError } = useSWR(
        `${BACKEND_ROUTES.COMPANY}?sort=name&limit=${PAGINATION.COMPANY.LIMIT.MAX}`
    );
    const { data: contracts, error: contractsFetchError } = useSWR(
        companyWatch
            ? `${BACKEND_ROUTES.CONTRACT}?sort=name&companyUuid=${companyWatch.uuid}&limit=${PAGINATION.CONTRACT.LIMIT.MAX}`
            : null
    );
    const { data: sites, error: sitesFetchError } = useSWR(
        contractWatch
            ? `${BACKEND_ROUTES.SITE}?sort=name&contractUuid=${contractWatch.uuid}`
            : null
    );
    const { data: missions, error: missionsFetchError } = useSWR(
        siteWatch
            ? `${BACKEND_ROUTES.MISSION}?sort=date&siteUuid=${siteWatch.uuid}`
            : null
    );

    const { data: selectedMissionGeometries } = useSWR(
        missionWatch?.uuid
            ? `${BACKEND_ROUTES.MISSION_GEOMETRY}?missionUuid=${missionWatch.uuid}`
            : null
    );

    const selectedMissionExistingGeometry = useMemo(
        () =>
            selectedMissionGeometries?.rows?.find(
                (g) => g.type === "Radiometric target"
            ) || null,
        [selectedMissionGeometries]
    );

    const mergedFetchError =
        contractsFetchError ??
        companiesFetchError ??
        sitesFetchError ??
        missionsFetchError;

    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;

    if (!companies) {
        return (
            <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height="25em"
            />
        );
    }

    const onSubmit = async (payload) => {
        if (!radioMetricTargetPolygonRef.current) return;

        setLoading(true);

        try {
            const currentGeoJSON =
                radioMetricTargetPolygonRef.current.toGeoJSON();
            const currentGeometry = currentGeoJSON.geometry;

            // Save to database if mission exists
            if (payload.mission?.uuid) {
                await saveMissionGeometry({
                    patch,
                    post,
                    missionUuid: payload.mission.uuid,
                    existingGeometry: selectedMissionExistingGeometry,
                    geometry: currentGeometry,
                    name: payload.radioMetricTargetName,
                    type: "Radiometric target",
                    refetchMissionGeometries,
                    openSnackbar,
                });
            }

            // Update popup with new name
            if (radioMetricTargetPolygonRef.current) {
                radioMetricTargetPolygonRef.current.setPopupContent(
                    `Radiometric Target: ${payload.radioMetricTargetName}`
                );
            }

            if (payload.export) {
                onExport(currentGeoJSON, payload.radioMetricTargetName);
            }

            setLoading(false);
            setIsOpen(false);
        } catch (error) {
            setLoading(false);
            openSnackbar(
                error.message || "Failed to save radiometric target",
                "error"
            );
        }
    };

    const onExport = (geoJSON, targetName) => {
        const geoJsonData = {
            type: "FeatureCollection",
            features: [
                {
                    ...geoJSON,
                    properties: {
                        ...geoJSON.properties,
                        plot_id: targetName,
                    },
                },
            ],
        };

        download(JSON.stringify(geoJsonData), "radiometric_target.geojson");
    };

    const onExportOnly = async () => {
        if (!radioMetricTargetPolygonRef.current) return;

        const currentGeoJSON = radioMetricTargetPolygonRef.current.toGeoJSON();
        onExport(currentGeoJSON, radioMetricTargetNameWatch);
    };

    const onSaveAndExport = async (payload) => {
        await onSubmit({ ...payload, export: true });
    };

    return (
        <form>
            <Box sx={PARCELLAIRE_TAB_BOX_STYLE}>
                <Grid container spacing={3} alignItems="center">
                    <Grid size={12}>
                        <Controller
                            control={control}
                            name="company"
                            render={({
                                field: { ref, onChange, ...fieldProps },
                            }) => (
                                <Autocomplete
                                    {...fieldProps}
                                    id="company"
                                    options={companies.rows}
                                    isOptionEqualToValue={(option, value) =>
                                        option.uuid === value.uuid
                                    }
                                    getOptionLabel={(option) => option.name}
                                    onChange={(_, value) => {
                                        onChange(value);
                                        if (
                                            value &&
                                            contractWatch?.companyUuid !==
                                                value.uuid
                                        ) {
                                            setValue("contract", null);
                                            setValue("site", null);
                                            setValue("mission", null);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Company"
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Controller
                            control={control}
                            name="contract"
                            render={({
                                field: { ref, onChange, ...fieldProps },
                            }) => (
                                <Autocomplete
                                    {...fieldProps}
                                    id="contract"
                                    options={
                                        companyWatch
                                            ? (contracts?.rows ?? [])
                                            : []
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.uuid === value.uuid
                                    }
                                    getOptionLabel={(option) => option.name}
                                    onChange={(_, value) => {
                                        onChange(value);
                                        if (
                                            siteWatch?.contractUuid !==
                                            value?.uuid
                                        ) {
                                            setValue("site", null);
                                            setValue("mission", null);
                                        }
                                    }}
                                    disabled={!companyWatch}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Campaign"
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Controller
                            control={control}
                            name="site"
                            render={({
                                field: { ref, onChange, ...fieldProps },
                            }) => (
                                <Autocomplete
                                    {...fieldProps}
                                    id="site"
                                    options={
                                        contractWatch ? (sites?.rows ?? []) : []
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.uuid === value.uuid
                                    }
                                    getOptionLabel={(option) => option.name}
                                    onChange={(_, value) => {
                                        onChange(value);
                                        if (
                                            missionWatch?.siteUuid !==
                                            value?.uuid
                                        ) {
                                            setValue("mission", null);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Site"
                                        />
                                    )}
                                    disabled={!contractWatch || !sites}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Controller
                            control={control}
                            name="mission"
                            render={({
                                field: { ref, onChange, ...fieldProps },
                            }) => (
                                <Autocomplete
                                    {...fieldProps}
                                    id="mission"
                                    options={
                                        siteWatch ? (missions?.rows ?? []) : []
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.uuid === value.uuid
                                    }
                                    getOptionLabel={(option) =>
                                        formatDateTime(
                                            offset(new Date(option.date), true)
                                        )
                                    }
                                    onChange={(_, value) => {
                                        onChange(value);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Mission"
                                        />
                                    )}
                                    disabled={!siteWatch || !missions}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Controller
                            control={control}
                            name="radioMetricTargetName"
                            render={({
                                field: { ref, onChange, ...fieldProps },
                            }) => (
                                <Autocomplete
                                    {...fieldProps}
                                    id="radioMetricTargetName"
                                    options={Object.values(
                                        RADIOMETRIC_TARGET_NAMES
                                    )}
                                    onChange={(_, value) => onChange(value)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Radiometric Target Name"
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                <DialogActions>
                    <Button type="button" onClick={() => setIsOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={
                            loading ? (
                                <CircularProgress size="1em" />
                            ) : (
                                <FileDownloadIcon />
                            )
                        }
                        type="submit"
                        disabled={loading || !radioMetricTargetNameWatch}
                        onClick={handleSubmit(onExportOnly)}
                    >
                        Export
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={
                            loading ? (
                                <CircularProgress size="1em" />
                            ) : (
                                <AddCircleIcon />
                            )
                        }
                        type="submit"
                        disabled={
                            loading ||
                            !missionWatch ||
                            !radioMetricTargetNameWatch
                        }
                        onClick={handleSubmit(onSubmit)}
                    >
                        Save
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={
                            loading ? (
                                <CircularProgress size="1em" />
                            ) : (
                                <FileDownloadIcon />
                            )
                        }
                        type="submit"
                        disabled={
                            loading ||
                            !missionWatch ||
                            !radioMetricTargetNameWatch
                        }
                        onClick={handleSubmit(onSaveAndExport)}
                    >
                        Save & Export
                    </Button>
                </DialogActions>
            </Box>
        </form>
    );
}
